import { COLORS } from "../shared/constants";
import { IconBack, IconLock } from "../shared/icons";
import { calcTotalPulses, calcDuration, formatDuration } from "../shared/utils";
import { useState } from "react";

export default // ─── Protocol Form (Create / Edit) ──────────────────────────
function ProtocolForm({ protocol, currentUser, onSave, onCancel, embedded, coils, treatmentSpots, mtSpots }) {
  const isEdit = !!protocol;
  const isReadOnly = protocol?.is_system_default;

  // Dropdown options from API data (passed as props) with fallbacks
  const COILS = (coils || []).filter(c => c.is_active !== false).map(c => c.name || c);
  const TREATMENT_SPOTS = (treatmentSpots || []).filter(s => s.is_active !== false).map(s => s.name || s);
  const MT_SPOTS = (mtSpots || []).filter(s => s.is_active !== false).map(s => s.name || s);

  const [form, setForm] = useState({
    name: protocol?.name || "",
    treatment_type: protocol?.treatment_type || "RTMS",
    frequency_hz: protocol?.frequency_hz || "",
    train_pulses: protocol?.train_pulses || "",
    num_trains: protocol?.num_trains || "",
    inter_train_interval_sec: protocol?.inter_train_interval_sec || "",
    pulses_in_burst: protocol?.pulses_in_burst || "",
    inter_pulse_interval_ms: protocol?.inter_pulse_interval_ms || "",
    treatment_coil: protocol?.treatment_coil || "Cool B-65",
    treatment_spot: protocol?.treatment_spot || "Left DLPFC",
    treatment_ratio_pct: protocol?.treatment_ratio_pct || 120,
    intensity_is_machine_pct: protocol?.intensity_is_machine_pct || false,
    mt_coil: protocol?.mt_coil || "C-B60",
    mt_spot: protocol?.mt_spot || "rAPB",
    default_intensity_pct: protocol?.default_intensity_pct || "",
    max_intensity_pct: protocol?.max_intensity_pct || "",
    fda_approved: protocol?.fda_approved || false,
    md_approval_confirmed: false,
  });
  const [errors, setErrors] = useState({});
  const [ipiMode, setIpiMode] = useState("ms"); // "ms" or "hz"

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    setErrors((e) => ({ ...e, [field]: null }));
  };

  const numVal = (v) => (v === "" || v === null || v === undefined) ? 0 : Number(v);
  const totalPulses = calcTotalPulses({ ...form, train_pulses: numVal(form.train_pulses), num_trains: numVal(form.num_trains), pulses_in_burst: numVal(form.pulses_in_burst) });
  const duration = calcDuration({ ...form, frequency_hz: numVal(form.frequency_hz), train_pulses: numVal(form.train_pulses), num_trains: numVal(form.num_trains), inter_train_interval_sec: numVal(form.inter_train_interval_sec) });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.frequency_hz) errs.frequency_hz = "Required";
    if (!form.train_pulses) errs.train_pulses = "Required";
    if (!form.num_trains) errs.num_trains = "Required";
    if (form.treatment_type === "TBS" && !form.pulses_in_burst) errs.pulses_in_burst = "Required for TBS";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const submitData = {
      ...protocol,
      ...form,
      frequency_hz: numVal(form.frequency_hz),
      train_pulses: numVal(form.train_pulses),
      num_trains: numVal(form.num_trains),
      inter_train_interval_sec: numVal(form.inter_train_interval_sec),
      treatment_ratio_pct: numVal(form.treatment_ratio_pct),
      pulses_in_burst: form.treatment_type === "TBS" ? numVal(form.pulses_in_burst) : null,
      inter_pulse_interval_ms: form.treatment_type === "TBS" ? numVal(form.inter_pulse_interval_ms) : null,
      default_intensity_pct: form.treatment_type === "MPNS" ? numVal(form.default_intensity_pct) : null,
      max_intensity_pct: form.treatment_type === "MPNS" ? numVal(form.max_intensity_pct) : null,
      total_pulses: totalPulses,
      approx_duration_sec: duration,
    };
    console.log("[ProtocolForm] Saving:", submitData.id, submitData.name, "trains:", submitData.num_trains, "pulses:", submitData.total_pulses);
    onSave(submitData);
  };

  const inputStyle = (field) => ({
    width: "100%", height: "44px", padding: "0 14px", borderRadius: "8px",
    border: `1.5px solid ${errors[field] ? COLORS.danger : COLORS.border}`,
    background: isReadOnly ? "#F0F0F0" : COLORS.inputBg,
    fontSize: "14px", color: COLORS.text, outline: "none", boxSizing: "border-box",
  });
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: COLORS.textSecondary, marginBottom: "6px", display: "block" };

  return (
    <div style={embedded ? {} : { padding: "24px", maxWidth: "780px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: embedded ? "20px" : "28px" }}>
        <button onClick={onCancel} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}>
          <IconBack />
        </button>
        <h2 style={{ color: COLORS.text, fontSize: embedded ? "16px" : "20px", fontWeight: "600", margin: 0 }}>
          {isReadOnly ? "View Protocol" : isEdit ? "Edit Protocol" : "New Protocol"}
        </h2>
        {isReadOnly && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: COLORS.textSecondary, background: COLORS.inputBg, padding: "4px 10px", borderRadius: "6px" }}>
            <IconLock /> System Default (Read-Only)
          </span>
        )}
      </div>

      <div style={embedded ? {} : { background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, padding: "28px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>

        {/* Protocol Name + Type */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px", marginBottom: "20px" }}>
          <div>
            <label style={labelStyle}>Protocol Name *</label>
            <input style={inputStyle("name")} value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="e.g., Custom iTBS" disabled={isReadOnly}
              onFocus={(e) => (e.target.style.borderColor = COLORS.primary)} onBlur={(e) => (e.target.style.borderColor = errors.name ? COLORS.danger : COLORS.border)} />
            {errors.name && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.name}</span>}
          </div>
          <div>
            <label style={labelStyle}>Treatment Type *</label>
            <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
              {[["RTMS", "rTMS"], ["TBS", "Theta Burst"], ["MPNS", "mPNS"]].map(([val, label]) => (
                <button key={val} onClick={() => !isReadOnly && handleChange("treatment_type", val)}
                  style={{
                    flex: 1, height: "44px", borderRadius: "8px", border: `1.5px solid ${form.treatment_type === val ? COLORS.primary : COLORS.border}`,
                    background: form.treatment_type === val ? COLORS.primaryLight : COLORS.inputBg,
                    color: form.treatment_type === val ? COLORS.primary : COLORS.textSecondary,
                    fontSize: "13px", fontWeight: "600", cursor: isReadOnly ? "default" : "pointer",
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Visual Diagram — toggles between rTMS and TBS */}
        <div style={{
          background: "#1A1F2E", border: `1px solid #2A3040`, borderRadius: "10px",
          padding: "16px 20px", marginBottom: "24px",
        }}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>
            {form.treatment_type === "TBS" ? "Theta Burst Diagram" : "Pulse Train Diagram"}
          </div>
          {form.treatment_type === "TBS" ? (
            /* ── TBS Diagram ── */
            <svg viewBox="0 0 620 130" style={{ width: "100%", height: "130px" }}>
              {/* Amplitude arrow */}
              <line x1="30" y1="15" x2="30" y2="85" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
              <polygon points="30,15 27,22 33,22" fill="rgba(255,255,255,0.4)" />
              <polygon points="30,85 27,78 33,78" fill="rgba(255,255,255,0.4)" />
              <text x="18" y="52" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="600" transform="rotate(-90,18,52)">{form.intensity_is_machine_pct ? `${numVal(form.treatment_ratio_pct)}%` : `${numVal(form.treatment_ratio_pct)}% MT`}</text>

              {/* Burst 1 */}
              {[0,1,2].map((i) => (
                <line key={`b1-${i}`} x1={55 + i * 10} y1="20" x2={55 + i * 10} y2="80" stroke="#FFFFFF" strokeWidth="2.5" />
              ))}
              {/* IPI label between pulses */}
              <line x1="56" y1="12" x2="74" y2="12" stroke="#F39C12" strokeWidth="1" />
              <text x="65" y="9" textAnchor="middle" fill="#F39C12" fontSize="8" fontWeight="600">
                {ipiMode === "hz" ? `${numVal(form.inter_pulse_interval_ms) > 0 ? Math.round(1000 / numVal(form.inter_pulse_interval_ms)) : "—"}Hz` : `${numVal(form.inter_pulse_interval_ms)}ms`}
              </text>
              {/* Burst label */}
              <line x1="50" y1="90" x2="80" y2="90" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <text x="65" y="100" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="8" fontWeight="600">{numVal(form.pulses_in_burst)} pulses</text>

              {/* Burst 2 */}
              {[0,1,2].map((i) => (
                <line key={`b2-${i}`} x1={120 + i * 10} y1="20" x2={120 + i * 10} y2="80" stroke="#FFFFFF" strokeWidth="2.5" />
              ))}

              {/* Burst 3 */}
              {[0,1,2].map((i) => (
                <line key={`b3-${i}`} x1={185 + i * 10} y1="20" x2={185 + i * 10} y2="80" stroke="#FFFFFF" strokeWidth="2.5" />
              ))}

              {/* ... more bursts */}
              <text x="230" y="55" fill="rgba(255,255,255,0.5)" fontSize="12">…</text>

              {/* Burst N in train */}
              {[0,1,2].map((i) => (
                <line key={`b4-${i}`} x1={260 + i * 10} y1="20" x2={260 + i * 10} y2="80" stroke="#FFFFFF" strokeWidth="2.5" />
              ))}

              {/* Train bracket */}
              <line x1="50" y1="110" x2="285" y2="110" stroke={COLORS.primary} strokeWidth="1.5" />
              <line x1="50" y1="106" x2="50" y2="114" stroke={COLORS.primary} strokeWidth="1.5" />
              <line x1="285" y1="106" x2="285" y2="114" stroke={COLORS.primary} strokeWidth="1.5" />
              <text x="167" y="123" textAnchor="middle" fill={COLORS.primary} fontSize="9" fontWeight="600">Train ({numVal(form.train_pulses)} bursts × {numVal(form.pulses_in_burst)} = {numVal(form.train_pulses) * numVal(form.pulses_in_burst)} pulses)</text>

              {/* Burst frequency label */}
              <line x1="55" y1="88" x2="120" y2="88" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="3 2" />
              <text x="88" y="86" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="8" fontWeight="600">{numVal(form.frequency_hz)}Hz</text>

              {/* ITI */}
              <line x1="295" y1="50" x2="380" y2="50" stroke="#F39C12" strokeWidth="1.5" strokeDasharray="4 3" />
              <text x="337" y="44" textAnchor="middle" fill="#F39C12" fontSize="9" fontWeight="600">ITI ({numVal(form.inter_train_interval_sec)}s)</text>

              {/* Train 2 (abbreviated) */}
              {[0,1,2].map((i) => (
                <line key={`t2b1-${i}`} x1={395 + i * 10} y1="20" x2={395 + i * 10} y2="80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
              ))}
              <text x="440" y="55" fill="rgba(255,255,255,0.4)" fontSize="12">…</text>
              {[0,1,2].map((i) => (
                <line key={`t2b2-${i}`} x1={465 + i * 10} y1="20" x2={465 + i * 10} y2="80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
              ))}

              {/* Cycle time bracket */}
              <line x1="50" y1="126" x2="490" y2="126" stroke="#F39C12" strokeWidth="1" strokeDasharray="3 2" />

              {/* Total info */}
              <text x="560" y="35" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9" fontWeight="600">Total Cycles</text>
              <text x="560" y="52" textAnchor="middle" fill="#FFFFFF" fontSize="16" fontWeight="700">{numVal(form.num_trains)}</text>
              <text x="560" y="72" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9" fontWeight="600">Total Pulses</text>
              <text x="560" y="89" textAnchor="middle" fill="#FFFFFF" fontSize="16" fontWeight="700">{totalPulses.toLocaleString()}</text>
            </svg>
          ) : (
            /* ── rTMS Diagram ── */
            <svg viewBox="0 0 600 80" style={{ width: "100%", height: "80px" }}>
              {/* Train 1 */}
              <rect x="20" y="20" width="120" height="30" rx="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
              {[0,1,2,3,4,5].map((i) => (
                <line key={`t1-${i}`} x1={30 + i * 18} y1="22" x2={30 + i * 18} y2="48" stroke="#FFFFFF" strokeWidth="2" />
              ))}
              <text x="80" y="65" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontWeight="600">Train ({numVal(form.train_pulses)} pulses)</text>
              {/* ITI */}
              <line x1="140" y1="35" x2="220" y2="35" stroke="#F39C12" strokeWidth="1.5" strokeDasharray="4 3" />
              <text x="180" y="28" textAnchor="middle" fill="#F39C12" fontSize="9" fontWeight="600">ITI ({numVal(form.inter_train_interval_sec)}s)</text>
              {/* Train 2 */}
              <rect x="220" y="20" width="120" height="30" rx="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
              {[0,1,2,3,4,5].map((i) => (
                <line key={`t2-${i}`} x1={230 + i * 18} y1="22" x2={230 + i * 18} y2="48" stroke="#FFFFFF" strokeWidth="2" />
              ))}
              {/* ITI 2 */}
              <line x1="340" y1="35" x2="420" y2="35" stroke="#F39C12" strokeWidth="1.5" strokeDasharray="4 3" />
              <text x="380" y="28" textAnchor="middle" fill="#F39C12" fontSize="9" fontWeight="600">ITI</text>
              {/* Dots */}
              <text x="445" y="40" fill="rgba(255,255,255,0.5)" fontSize="16" fontWeight="600">…</text>
              {/* Train N */}
              <rect x="470" y="20" width="100" height="30" rx="4" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
              {[0,1,2,3,4].map((i) => (
                <line key={`t3-${i}`} x1={480 + i * 18} y1="22" x2={480 + i * 18} y2="48" stroke="#FFFFFF" strokeWidth="2" />
              ))}
              <text x="520" y="65" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="9" fontWeight="600">Train {numVal(form.num_trains)}</text>
              {/* Frequency label */}
              <text x="30" y="14" fill="rgba(255,255,255,0.6)" fontSize="9" fontWeight="600">{numVal(form.frequency_hz)} Hz</text>
            </svg>
          )}
        </div>

        {/* Stimulation Parameters */}
        <div style={{ fontSize: "12px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>
          Stimulation Parameters
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
          <div>
            <label style={labelStyle}>Frequency (Hz) *</label>
            <input type="number" step="0.1" style={inputStyle("frequency_hz")} value={form.frequency_hz} onChange={(e) => handleChange("frequency_hz", e.target.value)} disabled={isReadOnly} />
            {errors.frequency_hz && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.frequency_hz}</span>}
          </div>
          <div>
            <label style={labelStyle}>Train Pulses *</label>
            <input type="number" style={inputStyle("train_pulses")} value={form.train_pulses} onChange={(e) => handleChange("train_pulses", e.target.value)} disabled={isReadOnly} />
            {errors.train_pulses && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.train_pulses}</span>}
          </div>
          <div>
            <label style={labelStyle}># of Trains *</label>
            <input type="number" style={inputStyle("num_trains")} value={form.num_trains} onChange={(e) => handleChange("num_trains", e.target.value)} disabled={isReadOnly} />
            {errors.num_trains && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.num_trains}</span>}
          </div>
          <div>
            <label style={labelStyle}>ITI (sec)</label>
            <input type="number" step="0.1" style={inputStyle()} value={form.inter_train_interval_sec} onChange={(e) => handleChange("inter_train_interval_sec", e.target.value)} disabled={isReadOnly} />
          </div>
        </div>

        {/* TBS-only fields */}
        {form.treatment_type === "TBS" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Pulses in Burst *</label>
              <input type="number" style={inputStyle("pulses_in_burst")} value={form.pulses_in_burst} onChange={(e) => handleChange("pulses_in_burst", e.target.value)} disabled={isReadOnly} />
              {errors.pulses_in_burst && <span style={{ fontSize: "12px", color: COLORS.danger }}>{errors.pulses_in_burst}</span>}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>Inter-Pulse Interval</label>
                <div style={{ display: "flex", gap: "2px" }}>
                  {[["ms", "ms"], ["hz", "Hz"]].map(([val, label]) => (
                    <button key={val} onClick={() => {
                      if (isReadOnly) return;
                      const currentMs = numVal(form.inter_pulse_interval_ms);
                      setIpiMode(val);
                      // No conversion needed — we always store in ms, just display differently
                    }}
                    style={{
                      fontSize: "10px", padding: "2px 8px", borderRadius: "4px",
                      border: `1px solid ${ipiMode === val ? COLORS.primary : COLORS.border}`,
                      background: ipiMode === val ? COLORS.primary : "transparent",
                      color: ipiMode === val ? "#FFF" : COLORS.textSecondary,
                      cursor: isReadOnly ? "default" : "pointer", fontWeight: "600",
                    }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {ipiMode === "ms" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <input type="number" style={inputStyle()} value={form.inter_pulse_interval_ms}
                    onChange={(e) => handleChange("inter_pulse_interval_ms", e.target.value)} disabled={isReadOnly} />
                  <span style={{ fontSize: "12px", color: COLORS.textSecondary, whiteSpace: "nowrap" }}>ms</span>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <input type="number" style={inputStyle()}
                    value={numVal(form.inter_pulse_interval_ms) > 0 ? Math.round(1000 / numVal(form.inter_pulse_interval_ms)) : ""}
                    onChange={(e) => {
                      const hz = numVal(e.target.value);
                      const ms = hz > 0 ? Math.round(1000 / hz) : "";
                      handleChange("inter_pulse_interval_ms", ms);
                    }} disabled={isReadOnly} />
                  <span style={{ fontSize: "12px", color: COLORS.textSecondary, whiteSpace: "nowrap" }}>Hz</span>
                </div>
              )}
              {numVal(form.inter_pulse_interval_ms) > 0 && (
                <div style={{ fontSize: "11px", color: COLORS.textSecondary, marginTop: "4px" }}>
                  = {form.inter_pulse_interval_ms}ms / {Math.round(1000 / numVal(form.inter_pulse_interval_ms))}Hz
                </div>
              )}
            </div>
            <div />
          </div>
        )}

        {/* mPNS-only fields */}
        {form.treatment_type === "MPNS" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Default Intensity %</label>
              <input type="number" min="1" max="100" style={inputStyle("default_intensity_pct")} value={form.default_intensity_pct} onChange={(e) => handleChange("default_intensity_pct", e.target.value)} disabled={isReadOnly} placeholder="20" />
            </div>
            <div>
              <label style={labelStyle}>Max Intensity %</label>
              <input type="number" min="1" max="100" style={inputStyle("max_intensity_pct")} value={form.max_intensity_pct} onChange={(e) => handleChange("max_intensity_pct", e.target.value)} disabled={isReadOnly} placeholder="50" />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "4px" }}>
              <span style={{ fontSize: "12px", color: COLORS.textSecondary }}>No MT required for mPNS</span>
            </div>
          </div>
        )}

        {/* Auto-calculated */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "24px" }}>
          <div style={{ padding: "14px 16px", background: "#F0FAF5", borderRadius: "8px", border: `1px solid #C8E6D9` }}>
            <div style={{ fontSize: "11px", fontWeight: "600", color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Pulses</div>
            <div style={{ fontSize: "24px", fontWeight: "700", color: COLORS.text, marginTop: "4px" }}>{totalPulses.toLocaleString()}</div>
          </div>
          <div style={{ padding: "14px 16px", background: "#F0FAF5", borderRadius: "8px", border: `1px solid #C8E6D9` }}>
            <div style={{ fontSize: "11px", fontWeight: "600", color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.5px" }}>Approx. Duration</div>
            <div style={{ fontSize: "24px", fontWeight: "700", color: COLORS.text, marginTop: "4px" }}>{formatDuration(duration)}</div>
          </div>
        </div>

        {/* Intensity */}
        <div style={{ fontSize: "12px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>
          Intensity & Coil Configuration
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <div>
            <label style={labelStyle}>Treatment Ratio</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input type="number" style={{ ...inputStyle(), width: "80px", flex: "none" }} value={form.treatment_ratio_pct} onChange={(e) => handleChange("treatment_ratio_pct", e.target.value)} disabled={isReadOnly} />
              <span style={{ fontSize: "13px", color: COLORS.textSecondary }}>
                {form.intensity_is_machine_pct ? "% machine output" : "% MT"}
              </span>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", fontSize: "12px", color: COLORS.textSecondary, cursor: isReadOnly ? "default" : "pointer" }}>
              <input type="checkbox" checked={form.intensity_is_machine_pct} onChange={(e) => handleChange("intensity_is_machine_pct", e.target.checked)} disabled={isReadOnly} />
              Machine output % (not MT-based)
            </label>
          </div>
          <div>
            <label style={labelStyle}>Treatment Coil</label>
            <select style={inputStyle()} value={form.treatment_coil} onChange={(e) => handleChange("treatment_coil", e.target.value)} disabled={isReadOnly}>
              {COILS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Treatment Spot</label>
            <select style={inputStyle()} value={form.treatment_spot} onChange={(e) => handleChange("treatment_spot", e.target.value)} disabled={isReadOnly}>
              {TREATMENT_SPOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px" }}>
          <div />
          <div>
            <label style={labelStyle}>MT Coil</label>
            <select style={inputStyle()} value={form.mt_coil} onChange={(e) => handleChange("mt_coil", e.target.value)} disabled={isReadOnly}>
              {COILS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>MT Spot</label>
            <select style={inputStyle()} value={form.mt_spot} onChange={(e) => handleChange("mt_spot", e.target.value)} disabled={isReadOnly}>
              {MT_SPOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <button onClick={onCancel}
            style={{ height: "44px", padding: "0 24px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontSize: "14px", fontWeight: "500", cursor: "pointer" }}>
            {isReadOnly ? "Back" : "Cancel"}
          </button>
          {!isReadOnly && (
            <button onClick={handleSubmit}
              style={{ height: "44px", padding: "0 28px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.primaryHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = COLORS.primary)}>
              {isEdit ? "Save Changes" : "Create Protocol"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Assessment Scales Settings Tab ──────────────────────────

